import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';

interface CustomerSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * Customer Portal Phase 4 follow-up — a customer-scoped search input using
 * the approved fixed palette directly, matching every other Customer Portal
 * control (CustomerLoginPage, CustomerPortalLayout, etc). The shared staff
 * `SearchBar`/`Input` components read theme CSS variables (bg-card,
 * border-input) that resolve dark under the app's system/dark theme mode —
 * that's fine for staff/platform screens, which are theme-aware, but wrong
 * here since the Customer Portal deliberately uses a fixed light palette
 * regardless of the visitor's OS theme preference. This component exists so
 * customer-portal screens never depend on that shared theme, without
 * altering the staff SearchBar's own behavior.
 */
export function CustomerSearchBar({
  value,
  onChange,
  placeholder = 'Search...',
}: CustomerSearchBarProps) {
  return (
    <div className="relative w-full max-w-sm">
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-customer-text-secondary"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label="Search"
        className="flex h-11 w-full rounded-lg border border-customer-border bg-customer-surface py-2 pl-9 pr-9 text-sm text-customer-text placeholder:text-customer-text-secondary transition-colors hover:border-customer-text/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus:border-customer-primary"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-0 top-0 flex h-11 w-9 items-center justify-center text-customer-text-secondary hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-inset"
          aria-label="Clear search"
        >
          <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
